import React, { useEffect } from 'react';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { ModalType } from '../../shared/enum/EnumRepository';
import { useModalStore } from '../../shared/store/portal/modalStore';
import {
  useWorkTagActions,
  useWorkTagState,
} from '../../shared/hooks/useApi/useWorkTag';
import HintSmallWrapper from '../../shared/component/headless/button/HintSmallWrapper';
import { getHintMessage } from '../../shared/constants/hintMessages';
import createIcon from '../../asset/icon/create.png';
import { WorkTagData } from '../../shared/dto/EntityRepository';
import editIcon from '../../asset/icon/edit.png';
import { useUnsavedChanges } from '../../shared/hooks/useUnsavedChanges';
import { usePageOwnerIdentityStore } from '../../shared/store/pageOwnerIdentityStore';
import { canShowWorkTagList } from '../../shared/util/visibility/workTagVisibility';

const WorkTagStation: React.FC = () => {
  const { isEditMode } = useEditMode();
  const { setStandardModal } = useModalStore();
  const {
    tags,
    selectedTags,
    listOwnerAui,
    status,
  } = useWorkTagState();
  const {
    getWorkTagList,
    addSelectedTag,
    removeSelectedTag,
  } = useWorkTagActions();
  const pageOwner = usePageOwnerIdentityStore((s) => s.pageOwner);
  const { requestExitEditMode } = useUnsavedChanges();
  const canShowTags = canShowWorkTagList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui,
    status,
  });
  const visibleTags = canShowTags ? tags : [];
  const visibleSelectedTags = canShowTags ? selectedTags : [];

  useEffect(() => {
    if (!pageOwner?.aui) return;

    const getTagListWithApi = async () => {
      await getWorkTagList();
    };
    getTagListWithApi();
  }, [pageOwner?.aui]);

  const handleTags = () => {
    setStandardModal({
      modalType: ModalType.WORK_TAG_STATION,
      title: 'Labels',
      value: '',
      handleChange: () => {},
    });
  };

  const handleCreateTag = () => {
    setStandardModal({
      modalType: ModalType.CREATE_WORK_TAG,
      title: 'Create Tag',
      value: '',
      handleChange: () => {},
    });
  };

  const handleSelectTag = (tag: WorkTagData) => {
    if (isEditMode) {
      requestExitEditMode({ confirmWhenClean: true });
      return;
    }
    if (selectedTags.some((t) => t.id === tag.id)) {
      removeSelectedTag(tag);
    } else {
      addSelectedTag(tag);
    }
  };

  const hasTags = visibleTags.length > 0;

  return (
    <WorkTagStationComp>
      {isEditMode && (
        <BtnContainer>
          <HintSmallWrapper content={getHintMessage('CHANGE_TAGS')}>
            <UniversalIconBtn
              size="fit"
              border={false}
              isTransparent
              backgroundImg={editIcon}
              onClick={handleTags}
            />
          </HintSmallWrapper>
          <HintSmallWrapper content={getHintMessage('ADD_TAG')}>
            <UniversalIconBtn
              size="small"
              border={false}
              isTransparent
              backgroundImg={createIcon}
              onClick={handleCreateTag}
            />
          </HintSmallWrapper>
        </BtnContainer>
      )}
      {hasTags && (
      <WorkTagFactorContainer>
        {visibleTags.map((tag) => (
          <WorkTagFactor
          key={tag.id}
          title={tag.name}
          $isSelected={visibleSelectedTags.some((t) => t.id === tag.id)}
          onClick={() => handleSelectTag(tag)}
          >
            {tag.name}
          </WorkTagFactor>
        ))}
      </WorkTagFactorContainer>
      )}
    </WorkTagStationComp>
  );
};

const WorkTagStationComp = styled.section`
  width: fit-content;
  opacity: 1;
  height: calc(2vh);

  display: flex;
  justify-content: flex-start;
  align-items: center;

  gap: 12px;
  transition: all 0.7s ease-in-out;
  z-index: 3;
`;

const WorkTagFactorContainer = styled.div`
  width: fit-content;
  max-width: 100%;
  min-width: 0;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 12px;
  overflow-x: auto;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const WorkTagFactor = styled.span <{ $isSelected: boolean }>`
  width: fit-content;
  flex-shrink: 0;
  border-bottom: 1px dotted ${({ $isSelected, theme }) => $isSelected ? theme.colors.color_Gray_03 : "transparent"};
  color: ${({ $isSelected, theme }) => $isSelected ? theme.colors.color_Gray_02 : theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
`;

const BtnContainer = styled.div`
  width: fit-content;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export default WorkTagStation;
